/**
 * Phase 21.3a5 Batch 2 — 📡 RGM Studio Live Promo Card.
 *
 * Static admin-controlled promo card surfaced on the homepage. Reads
 * `platform_settings.live_promo_card` via GET /api/homepage/config.
 * Renders nothing when disabled. NEVER touches LiveKit / LiveNowStrip.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { optimizeImage } from "@/lib/mediaUrls";

const LivePromoCard = () => {
  const [card, setCard] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/homepage/config")
      .then((r) => {
        if (cancelled) return;
        setCard(r.data?.live_promo_card || null);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || !card || !card.enabled) return null;

  const ctaLink = card.cta_link || "/live";
  const isExternal = ctaLink.startsWith("http");

  return (
    <div
      data-testid="live-promo-card"
      className="relative rounded-2xl overflow-hidden mb-6 border border-purple-500/40 bg-gradient-to-br from-purple-800/40 via-pink-700/30 to-amber-500/30"
    >
      {card.image_url && (
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: `url(${optimizeImage(card.image_url, 'card')})` }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
      <div className="relative p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] uppercase tracking-[3px] font-black text-amber-200 mb-1"
            data-testid="live-promo-card-badge"
          >
            📡 Now on RGM Studio Live
          </p>
          <h3
            className="text-xl sm:text-2xl font-black text-white leading-tight"
            data-testid="live-promo-card-title"
          >
            {card.title}
          </h3>
          {card.text && (
            <p
              className="text-sm text-pink-100/90 mt-1"
              data-testid="live-promo-card-text"
            >
              {card.text}
            </p>
          )}
        </div>
        {card.cta_label && (
          <Link
            to={ctaLink}
            target={isExternal ? "_blank" : "_self"}
            className="inline-flex items-center px-5 py-2.5 rounded-full bg-white text-black font-black text-sm hover:bg-amber-200 transition-colors self-start sm:self-auto"
            data-testid="live-promo-card-cta"
          >
            {card.cta_label} →
          </Link>
        )}
      </div>
    </div>
  );
};

export default LivePromoCard;
